"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useTabsState = useTabsState;
const tslib_1 = require("tslib");
const vue_demi_1 = require("vue-demi");
const request_1 = tslib_1.__importDefault(require("../assets/scripts/request"));
const fego_1 = require("../common/fego");
const useI18n_1 = require("../common/useI18n");
const fegoProps_1 = require("../models/fegoProps");
const transformers_1 = require("./transformers");
// 引入 Toast 用于显示 loading
const { useToast } = require('@fegobull/webapi').use;
/**
 * 从多语言 content 数组中提取指定语言的 title
 * @param content 多语言配置数组
 * @param languageId 语言 ID，默认 EN (2)
 */
function getTitleFromContent(content, languageId = fegoProps_1.LanguageId.EN) {
    var _a;
    const item = content.find((c) => c.languageId === languageId);
    return (item === null || item === void 0 ? void 0 : item.title) || ((_a = content[0]) === null || _a === void 0 ? void 0 : _a.title) || '';
}
/**
 * Tab 状态管理 composable
 * 处理 Tab 切换、滚动联动高亮等逻辑
 * 支持动态加载和生成 tabs
 */
function useTabsState(props, $emit) {
    var _a;
    // 初始化 Toast
    const Toast = useToast();
    // 动态 Tab 配置数据
    const tabs = (0, vue_demi_1.ref)([]);
    // 当前激活的 Tab
    const activeTabKey = (0, vue_demi_1.ref)('');
    // 滚动控制变量
    let isScrolling = false;
    let targetScrollPosition = 0;
    let ticking = false;
    // Tab 栏高度常量
    const TAB_HEIGHT = 80;
    /**
     * 获取 URL 中的 module_key 参数
     */
    const getModuleKeyFromUrl = () => {
        if (typeof window === 'undefined')
            return null;
        const params = new URLSearchParams(window.location.search);
        return params.get('module_key');
    };
    /**
     * 更新 URL 中的 module_key 参数
     */
    const updateModuleKeyInUrl = (key) => {
        if (typeof window === 'undefined')
            return;
        const url = new URL(window.location.href);
        if (url.searchParams.get('module_key') === key)
            return;
        url.searchParams.set('module_key', key);
        window.history.replaceState({}, '', url.toString());
    };
    /**
     * 根据 tab key 获取对应的 DOM 元素
     */
    const getModuleElement = (key) => {
        return document.getElementById(`module-${key}`);
    };
    /** 默认展示股票数量 */
    const DEFAULT_STOCK_COUNT = 2;
    /**
     * 加载所有模块数据并生成 tabs 配置
     */
    const loadTabsData = async () => {
        var _a, _b, _c, _d, _e;
        const languageId = (_a = props.language_id) !== null && _a !== void 0 ? _a : fegoProps_1.LanguageId.EN;
        const pageType = (_b = props.page_type) !== null && _b !== void 0 ? _b : fegoProps_1.SignalSelectedPageType.PRIMARY;
        const tabsResult = [];
        // 标志位：是否已关闭 loading
        let loadingClosed = false;
        Toast.loading(60);
        // 获取各模块的展示数量配置
        const signalStockCount = (_c = props.signal_stock_count) !== null && _c !== void 0 ? _c : DEFAULT_STOCK_COUNT;
        const ratingStockCount = (_d = props.rating_stock_count) !== null && _d !== void 0 ? _d : DEFAULT_STOCK_COUNT;
        const analystCount = (_e = props.analyst_stock_count) !== null && _e !== void 0 ? _e : DEFAULT_STOCK_COUNT;
        // 主页模式：并发加载所有开启的模块，每个模块加载完成后立即更新 tabs（渐进式渲染）
        // 计算每个模块的槽位 (slot)，用于保持顺序
        const signalSlots = []; // signal keys 按顺序
        let ratingSlot = -1;
        let analystSlot = -1;
        let currentSlot = 0;
        if (props.signal_visible && props.signal_key_list && props.signal_key_list.length > 0) {
            props.signal_key_list.forEach((item) => {
                signalSlots.push(item.key);
                currentSlot++;
            });
        }
        if (props.rating_visible) {
            ratingSlot = currentSlot++;
        }
        if (props.analyst_visible) {
            analystSlot = currentSlot++;
        }
        // 用于追踪已加载的模块 (slot index -> TabItem)
        const loadedTabs = new Map();
        // 辅助函数：根据 loadedTabs 重建 tabs 数组（保持槽位顺序）
        const rebuildTabs = () => {
            const sortedSlots = Array.from(loadedTabs.keys()).sort((a, b) => a - b);
            tabs.value = sortedSlots
                .map((slot) => loadedTabs.get(slot))
                .filter((t) => {
                var _a, _b, _c, _d, _e, _f;
                return ((_b = (_a = t.signalData) === null || _a === void 0 ? void 0 : _a.stockList) === null || _b === void 0 ? void 0 : _b.length) ||
                    ((_d = (_c = t.ratingData) === null || _c === void 0 ? void 0 : _c.stockList) === null || _d === void 0 ? void 0 : _d.length) ||
                    ((_f = (_e = t.analystData) === null || _e === void 0 ? void 0 : _e.analystList) === null || _f === void 0 ? void 0 : _f.length);
            });
            // 如果还没有设置 activeTabKey，设置为第一个（不滚动）
            if (!activeTabKey.value && tabs.value.length > 0) {
                activeTabKey.value = tabs.value[0].key;
            }
            // 如果至少有 1 个 tab 加载成功，且 loading 还未关闭，则关闭 loading
            if (tabs.value.length >= 1 && !loadingClosed) {
                Toast.close();
                loadingClosed = true;
            }
        };
        // 并发加载所有模块
        const loadPromises = [];
        // 1. Signal 模块（并发加载）
        signalSlots.forEach((signalKey, index) => {
            const loadSignal = async () => {
                var _a;
                try {
                    const requestParams = {
                        actSign: props.signal_actSign || '',
                        module: props.signal_module || '',
                        signalKey,
                        pageType
                    };
                    const { data: rsp = null } = await retry(async () => {
                        return request_1.default.getSignalSelectedStocks(requestParams).then((res) => {
                            var _a, _b, _c;
                            if (!((_c = (_b = (_a = res === null || res === void 0 ? void 0 : res.data) === null || _a === void 0 ? void 0 : _a.result) === null || _b === void 0 ? void 0 : _b.selectedStocks) === null || _c === void 0 ? void 0 : _c.length)) {
                                return Promise.reject(new Error('no data'));
                            }
                            return res;
                        });
                    });
                    if (!rsp) {
                        return;
                    }
                    const title = getTitleFromContent(rsp.config.content, languageId);
                    const signalData = (0, transformers_1.transformSignalSelectedStocksData)(rsp, languageId, signalStockCount);
                    // 从配置中获取对应的 view_more_url
                    const signalConfig = (_a = props.signal_key_list) === null || _a === void 0 ? void 0 : _a.find((item) => item.key === signalKey);
                    const viewMoreUrl = signalConfig === null || signalConfig === void 0 ? void 0 : signalConfig.view_more_url;
                    loadedTabs.set(index, {
                        key: signalKey,
                        label: title,
                        type: 'signal',
                        viewMoreUrl,
                        signalData
                    });
                    rebuildTabs();
                }
                catch (err) {
                    console.error(`Signal ${signalKey} load failed`, err);
                }
            };
            loadPromises.push(loadSignal());
        });
        // 2. Rating 模块
        if (ratingSlot >= 0) {
            const loadRating = async () => {
                var _a;
                try {
                    const requestParams = {
                        actSign: props.rating_actSign || '',
                        module: props.rating_module || '',
                        pageType
                    };
                    const { data: ratingRsp = null } = await retry(async () => {
                        return request_1.default.getRatingSelectedStocks(requestParams).then((res) => {
                            var _a, _b, _c;
                            if (!((_c = (_b = (_a = res === null || res === void 0 ? void 0 : res.data) === null || _a === void 0 ? void 0 : _a.result) === null || _b === void 0 ? void 0 : _b.selectedStocks) === null || _c === void 0 ? void 0 : _c.length)) {
                                return Promise.reject(new Error('no data'));
                            }
                            return res;
                        });
                    });
                    if (!ratingRsp) {
                        return;
                    }
                    const title = getTitleFromContent(ratingRsp.config.content, languageId);
                    const ratingData = (0, transformers_1.transformRatingSelectedStocksData)(ratingRsp, languageId, ratingStockCount);
                    if (((_a = ratingData === null || ratingData === void 0 ? void 0 : ratingData.stockList) === null || _a === void 0 ? void 0 : _a.length) > 0) {
                        loadedTabs.set(ratingSlot, {
                            key: 'rating',
                            label: title,
                            type: 'rating',
                            viewMoreUrl: props.rating_view_more_url,
                            ratingData
                        });
                        rebuildTabs();
                    }
                }
                catch (err) {
                    console.error('getRatingSelectedStocks error', err);
                }
            };
            loadPromises.push(loadRating());
        }
        // 3. Analyst 模块
        if (analystSlot >= 0) {
            const loadAnalyst = async () => {
                try {
                    const requestParams = {
                        actSign: props.analyst_actSign || '',
                        module: props.analyst_module || ''
                    };
                    const { data: analystRsp = null } = await retry(async () => {
                        return request_1.default.getAnalystUpdates(requestParams).then((res) => {
                            var _a, _b;
                            if (!((_b = (_a = res === null || res === void 0 ? void 0 : res.data) === null || _a === void 0 ? void 0 : _a.topAnalysts) === null || _b === void 0 ? void 0 : _b.length)) {
                                return Promise.reject(new Error('no data'));
                            }
                            return res;
                        });
                    });
                    if (!analystRsp) {
                        return;
                    }
                    const title = getTitleFromContent(analystRsp.config.content, languageId);
                    const analystData = (0, transformers_1.transformTopAnalystListData)(analystRsp, languageId, analystCount);
                    loadedTabs.set(analystSlot, {
                        key: 'analyst',
                        label: title,
                        type: 'analyst',
                        viewMoreUrl: props.analyst_view_more_url,
                        analystData
                    });
                    rebuildTabs();
                }
                catch (err) {
                    console.error('getAnalystUpdates error', err);
                }
            };
            loadPromises.push(loadAnalyst());
        }
        // 等待所有加载完成
        await Promise.all(loadPromises);
        // 如果所有请求都失败（没有加载成功任何 tab），兜底关闭 loading
        if (!loadingClosed) {
            Toast.close();
            loadingClosed = true;
            return Promise.reject(new Error('no data'));
        }
        // 所有请求完成后，处理 URL module_key 滚动
        const urlModuleKey = getModuleKeyFromUrl();
        if (urlModuleKey && tabs.value.length > 0) {
            const matchedTab = tabs.value.find((t) => t.key === urlModuleKey);
            if (matchedTab) {
                activeTabKey.value = matchedTab.key;
                (0, vue_demi_1.nextTick)(() => {
                    handleTabClick(matchedTab.key, false);
                });
            }
        }
        // 如果没有 module_key 或没有对应模块，不滚动（activeTabKey 已在 rebuildTabs 中设置为第一个）
    };
    /**
     * 点击 Tab 跳转到对应模块
     */
    const handleTabClick = (key, smooth = true) => {
        // 立即更新激活状态
        activeTabKey.value = key;
        // 更新 URL 中的 module_key
        updateModuleKeyInUrl(key);
        const targetEl = getModuleElement(key);
        if (targetEl) {
            targetScrollPosition = targetEl.getBoundingClientRect().top + window.scrollY - TAB_HEIGHT;
            // 标记正在滚动，防止滚动联动干扰
            isScrolling = true;
            window.scrollTo({
                top: targetScrollPosition,
                behavior: smooth ? 'smooth' : 'auto'
            });
        }
    };
    /**
     * 滚动监听：联动高亮当前可见模块对应的 Tab
     */
    const handleScroll = () => {
        // 点击跳转过程中，检测是否到达目标位置
        if (isScrolling) {
            // 当滚动位置接近目标位置时（误差 10px 内），恢复联动
            if (Math.abs(window.scrollY - targetScrollPosition) < 10) {
                isScrolling = false;
            }
            return;
        }
        // 如果没有 tabs，不处理
        if (tabs.value.length === 0) {
            return;
        }
        // 找到当前视口中最靠近顶部的模块
        let currentKey = tabs.value[0].key;
        let minDistance = Infinity;
        for (const tab of tabs.value) {
            const el = getModuleElement(tab.key);
            if (el) {
                const rect = el.getBoundingClientRect();
                const distance = Math.abs(rect.top - TAB_HEIGHT);
                // 如果模块顶部在 Tab 栏下方附近，认为是当前模块
                if (rect.top <= TAB_HEIGHT + 100 && distance < minDistance) {
                    minDistance = distance;
                    currentKey = tab.key;
                }
            }
        }
        if (activeTabKey.value !== currentKey) {
            activeTabKey.value = currentKey;
            updateModuleKeyInUrl(currentKey);
        }
    };
    /**
     * 节流处理滚动事件
     */
    const onScroll = () => {
        if (!ticking) {
            window.requestAnimationFrame(() => {
                handleScroll();
                ticking = false;
            });
            ticking = true;
        }
    };
    const $t = (0, useI18n_1.useI18n)((_a = props.language_id) !== null && _a !== void 0 ? _a : fegoProps_1.LanguageId.EN);
    // 生命周期：加载数据、注册/移除滚动监听
    (0, vue_demi_1.onMounted)(() => {
        loadTabsData().catch((err) => {
            Toast.error($t('noData'));
            $emit(fego_1.EVENT_NAME_MAP.dataEmpty);
        });
        window.addEventListener('scroll', onScroll, { passive: true });
    });
    (0, vue_demi_1.onUnmounted)(() => {
        window.removeEventListener('scroll', onScroll);
    });
    return {
        tabs,
        activeTabKey,
        handleTabClick
    };
}
/**
 * 多次重试功能, 请求异常或者status_code!==200
 * @param {any} asyncFunc
 * @param {number} [retryTime=3]
 * @returns
 */
async function retry(asyncFunc, retryTime = 3) {
    return new Promise((resolve, reject) => {
        function reTryFunc(times) {
            asyncFunc()
                .then((res) => {
                resolve(res);
            })
                .catch((error) => {
                if (times > 0) {
                    reTryFunc(times - 1);
                }
                else {
                    reject(error);
                }
            });
        }
        reTryFunc(retryTime);
    });
}
//# sourceMappingURL=data:application/json;base64,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